import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { appointmentAPI, nailTechAPI, serviceAPI, adminAPI, depositAPI } from '../services/api';
import { formatUSPhoneInput, toTwilioUSPhone } from '../utils/phone';
import { Skeleton } from './Skeleton';
import './AdminDashboard.css';

export default function AdminDashboard() {
  const [appointments, setAppointments] = useState([]);
  const [nailTechs, setNailTechs] = useState([]);
  const [services, setServices] = useState([]);
  const [depositStats, setDepositStats] = useState({ required: 0, pending: 0, paid: 0 });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [showAddTech, setShowAddTech] = useState(false);
  const [showAddService, setShowAddService] = useState(false);
  const [showSMSSettings, setShowSMSSettings] = useState(false);
  const [showLegalContent, setShowLegalContent] = useState(false);
  const [showPaymentSettings, setShowPaymentSettings] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    zelleEmail: '',
    zellePhone: '',
    cashappTag: '',
    depositNote: '',
  });
  const [paymentMsg, setPaymentMsg] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  const [techForm, setTechForm] = useState({ name: '', phone: '', specialties: '' });
  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    price: '',
    duration: '',
    category: 'other',
    depositEnabled: false,
    depositType: 'fixed',
    depositValue: '',
  });

  const [twilioForm, setTwilioForm] = useState({
    reminderMessageTemplate: '',
    smsSchedule: {
      sendConfirmationOnCreate: false,
      send24hReminder: true,
      send1hReminder: false,
    },
    privacyPolicyContent: '',
    termsOfServiceContent: '',
  });
  const [twilioConfigured, setTwilioConfigured] = useState(false);
  const [twilioFromNumber, setTwilioFromNumber] = useState('');
  const [smsMsg, setSmsMsg] = useState('');
  const [testTo, setTestTo] = useState('');
  const [testMessage, setTestMessage] = useState('Test message from BookingApp');
  const [testStatus, setTestStatus] = useState('');

  const formatTimeAgo = (date) => {
    if (!date) return '';
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 5) return 'just now';
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    const hours = Math.floor(diff / 3600);
    return `${hours}h ago`;
  };

  const withTimeout = useCallback(
    (promise, timeoutMs, label) =>
      Promise.race([
        promise,
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error(`${label} request timed out`)), timeoutMs)
        ),
      ]),
    []
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [aptsRes, techsRes, servicesRes, depositRes] = await Promise.allSettled([
        withTimeout(appointmentAPI.getCount(), 12000, 'Appointments'),
        withTimeout(nailTechAPI.getAll(), 12000, 'Nail technicians'),
        withTimeout(serviceAPI.getAll(), 12000, 'Services'),
        withTimeout(depositAPI.getStats(), 12000, 'Deposits'),
      ]);

      if (aptsRes.status === 'fulfilled') {
        const countData = aptsRes.value.data || {};
        // Store count as pseudo-appointments array length for stat cards
        setAppointments(Array.from({ length: countData.count || 0 }));
      } else {
        console.error('Failed to fetch appointment count:', aptsRes.reason);
        setAppointments([]);
      }

      if (depositRes.status === 'fulfilled') {
        setDepositStats(depositRes.value.data || { required: 0, pending: 0, paid: 0 });
      } else {
        console.error('Failed to fetch deposit stats:', depositRes.reason);
        setDepositStats({ required: 0, pending: 0, paid: 0 });
      }

      if (techsRes.status === 'fulfilled') {
        setNailTechs(techsRes.value.data || []);
      } else {
        console.error('Failed to fetch nail technicians:', techsRes.reason);
        setNailTechs([]);
      }

      if (servicesRes.status === 'fulfilled') {
        setServices(servicesRes.value.data || []);
      } else {
        console.error('Failed to fetch services:', servicesRes.reason);
        setServices([]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
      setLastUpdated(new Date());
    }
  }, [withTimeout]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadSMSSettings = useCallback(async () => {
    try {
      const res = await adminAPI.getSettings();
      setTwilioForm({
        reminderMessageTemplate: res.data.reminderMessageTemplate || '',
        smsSchedule: {
          sendConfirmationOnCreate: Boolean(res.data?.smsSchedule?.sendConfirmationOnCreate),
          send24hReminder: res.data?.smsSchedule?.send24hReminder !== false,
          send1hReminder: Boolean(res.data?.smsSchedule?.send1hReminder),
        },
        privacyPolicyContent: res.data.privacyPolicyContent || '',
        termsOfServiceContent: res.data.termsOfServiceContent || '',
      });
      setTwilioConfigured(Boolean(res.data.twilioConfigured));
      setTwilioFromNumber(res.data.twilioPhoneNumber || '');
      setSmsMsg('');
    } catch (e) {
      setSmsMsg(e?.response?.data?.error || e?.message || 'Unable to load SMS settings');
    }
  }, []);

  useEffect(() => {
    if (showSMSSettings) {
      loadSMSSettings();
    }
  }, [showSMSSettings, loadSMSSettings]);

  const loadPaymentSettings = useCallback(async () => {
    try {
      setPaymentLoading(true);
      const res = await depositAPI.getPublicMethods();
      setPaymentForm({
        zelleEmail: res.data.zelleEmail || '',
        zellePhone: res.data.zellePhone || '',
        cashappTag: res.data.cashappTag || '',
        depositNote: res.data.depositNote || '',
      });
      setPaymentMsg('');
    } catch (e) {
      setPaymentMsg(e?.response?.data?.error || e?.message || 'Unable to load payment settings');
    } finally {
      setPaymentLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showPaymentSettings) {
      loadPaymentSettings();
    }
  }, [showPaymentSettings, loadPaymentSettings]);

  const savePaymentSettings = async (e) => {
    e.preventDefault();
    setPaymentLoading(true);
    setPaymentMsg('');
    try {
      await adminAPI.updateDepositSettings(paymentForm);
      setPaymentMsg('Deposit payment settings saved');
      toast.success('Deposit payment settings saved');
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to save payment settings';
      setPaymentMsg(msg);
      toast.error(msg);
    } finally {
      setPaymentLoading(false);
    }
  };

  const saveSMSSettings = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.updateSettings(twilioForm);
      setSmsMsg('Reminder and legal settings saved');
      await loadSMSSettings();
    } catch (e) {
      setSmsMsg(e?.response?.data?.error || e?.message || 'Failed to save settings');
    }
  };

  const sendTestSMS = async (e) => {
    e.preventDefault();
    setTestStatus('Sending test SMS...');
    try {
      if (!testTo.trim()) {
        setTestStatus('Enter a destination number');
        return;
      }
      const normalizedTo = toTwilioUSPhone(testTo);
      if (!normalizedTo) {
        setTestStatus('Enter a valid destination number');
        return;
      }

      const resp = await adminAPI.sendTestSMS(normalizedTo, testMessage);
      setTestStatus(`Sent: ${resp.data.sid || 'OK'}`);
    } catch (e) {
      const rawError = e?.response?.data?.error || e?.message || 'Failed to send test SMS';
      const rawCode = e?.response?.data?.code || '';
      const status = rawCode ? `${rawCode}: ${rawError}` : rawError;
      setTestStatus(status);
    }
  };


  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="stats">
          <Skeleton.Card height={100} />
          <Skeleton.Card height={100} />
          <Skeleton.Card height={100} />
        </div>
        <Skeleton.Table rows={5} />
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h2>Admin Dashboard</h2>
        {lastUpdated && (
          <span
            className="last-updated"
            title="Click to refresh"
            onClick={fetchData}
            style={{ fontSize: '0.72rem', color: 'var(--app-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Updated {formatTimeAgo(lastUpdated)}
          </span>
        )}
      </div>

      <div className="add-forms">
        <button onClick={() => setShowAddTech((s) => !s)} className="btn-add">{showAddTech ? 'Close Add Tech' : 'Add Tech'}</button>
        <button onClick={() => setShowAddService((s) => !s)} className="btn-add">{showAddService ? 'Close Add Service' : 'Add Service'}</button>

        {showAddTech && (
          <form className="add-form" onSubmit={async (e) => {
            e.preventDefault();
            try {
              const payload = { ...techForm, specialties: techForm.specialties.split(',').map((s) => s.trim()).filter(Boolean) };
              await nailTechAPI.create(payload);
              setTechForm({ name: '', phone: '', specialties: '' });
              setShowAddTech(false);
              fetchData();
              toast.success('Nail technician added');
            } catch (err) {
              toast.error('Failed to add technician');
            }
          }}>
            <h4>Add Technician</h4>
            <input placeholder="Name" value={techForm.name} onChange={(e) => setTechForm({ ...techForm, name: e.target.value })} required />
            <input placeholder="Phone" value={techForm.phone} onChange={(e) => setTechForm({ ...techForm, phone: e.target.value })} />
            <input placeholder="Specialties (comma separated)" value={techForm.specialties} onChange={(e) => setTechForm({ ...techForm, specialties: e.target.value })} />
            <button type="submit">Save Technician</button>
          </form>
        )}

        {showAddService && (
          <form className="add-form" onSubmit={async (e) => {
            e.preventDefault();
            try {
              const payload = {
                name: serviceForm.name,
                description: serviceForm.description,
                price: Number(serviceForm.price) || 0,
                duration: Number(serviceForm.duration) || 0,
                category: serviceForm.category,
                deposit: {
                  enabled: serviceForm.depositEnabled,
                  type: serviceForm.depositType === 'percent' ? 'percent' : 'fixed',
                  value: Number(serviceForm.depositValue) || 0,
                },
              };
              await serviceAPI.create(payload);
              setServiceForm({ name: '', description: '', price: '', duration: '', category: 'other', depositEnabled: false, depositType: 'fixed', depositValue: '' });
              setShowAddService(false);
              fetchData();
              toast.success('Service added');
            } catch (err) {
              toast.error('Failed to add service');
            }
          }}>
            <h4>Add Service</h4>
            <input placeholder="Name" value={serviceForm.name} onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })} required />
            <input placeholder="Description" value={serviceForm.description} onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })} />
            <div className="form-row-1">
              <input type="number" placeholder="Price ($)" value={serviceForm.price} onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value })} />
              <input type="number" placeholder="Duration (min)" value={serviceForm.duration} onChange={(e) => setServiceForm({ ...serviceForm, duration: e.target.value })} />
            </div>
            <select value={serviceForm.category} onChange={(e) => setServiceForm({ ...serviceForm, category: e.target.value })}>
              <option value="manicure">Manicure</option>
              <option value="pedicure">Pedicure</option>
              <option value="gel">Gel</option>
              <option value="acrylic">Acrylic</option>
              <option value="nail-art">Nail Art</option>
              <option value="other">Other</option>
            </select>
            <label className="deposit-toggle-label">
              <input
                type="checkbox"
                checked={serviceForm.depositEnabled}
                onChange={(e) => setServiceForm({ ...serviceForm, depositEnabled: e.target.checked })}
              />
              Require a deposit for this service
            </label>
            {serviceForm.depositEnabled && (
              <div className="form-row-1">
                <select value={serviceForm.depositType} onChange={(e) => setServiceForm({ ...serviceForm, depositType: e.target.value })}>
                  <option value="fixed">Fixed $</option>
                  <option value="percent">% of price</option>
                </select>
                <input
                  type="number"
                  min="0"
                  placeholder={serviceForm.depositType === 'percent' ? 'e.g. 25' : 'e.g. 15'}
                  value={serviceForm.depositValue}
                  onChange={(e) => setServiceForm({ ...serviceForm, depositValue: e.target.value })}
                />
              </div>
            )}
            <button type="submit">Save Service</button>
          </form>
        )}
      </div>

      <div className="stats">
        <div className="stat-card">
          <h3>{appointments.length}</h3>
          <p>Total Appointments</p>
        </div>
        <div className="stat-card">
          <h3>{nailTechs.length}</h3>
          <p>Nail Technicians</p>
        </div>
        <div className="stat-card">
          <h3>{services.length}</h3>
          <p>Services</p>
        </div>
        <div className="stat-card deposit-stats-card">
          <h3>Deposits</h3>
          <p className="deposit-summary">
            <span className="dep-count dep-required">{depositStats.required} Required</span>
            <span className="dep-count dep-pending">{depositStats.pending} Pending</span>
            <span className="dep-count dep-paid">{depositStats.paid} Paid</span>
          </p>
        </div>
      </div>

      <div className="sms-settings">
        <div className="sms-settings-header">
          <h3>SMS Reminder Settings</h3>
          <button className="btn-add" onClick={() => setShowSMSSettings((s) => !s)}>
            {showSMSSettings ? 'Hide' : 'Configure'}
          </button>
        </div>

        {showSMSSettings && (
          <div className="sms-settings-body">
            <form className="twilio-form" onSubmit={saveSMSSettings}>
              <p className="hint" style={{ marginTop: '0' }}>
                Twilio status: {twilioConfigured ? 'Configured' : 'Not configured'}
                {twilioFromNumber ? ` | From: ${twilioFromNumber}` : ''}
              </p>
              <div className="form-row-1">
                <div>
                  <label>Reminder Frequency Options</label>
                  <div className="sms-option-grid">
                    <label className="sms-option-card">
                      <input
                        type="checkbox"
                        checked={Boolean(twilioForm.smsSchedule?.sendConfirmationOnCreate)}
                        onChange={(e) => setTwilioForm({
                          ...twilioForm,
                          smsSchedule: {
                            ...twilioForm.smsSchedule,
                            sendConfirmationOnCreate: e.target.checked,
                          },
                        })}
                      />
                      <div>
                        <strong>Confirmation</strong>
                        <p>Send confirmation when appointment is saved</p>
                      </div>
                    </label>
                    <label className="sms-option-card">
                      <input
                        type="checkbox"
                        checked={twilioForm.smsSchedule?.send24hReminder !== false}
                        onChange={(e) => setTwilioForm({
                          ...twilioForm,
                          smsSchedule: {
                            ...twilioForm.smsSchedule,
                            send24hReminder: e.target.checked,
                          },
                        })}
                      />
                      <div>
                        <strong>24 Hour Reminder</strong>
                        <p>Send reminder 24 hours before appointment</p>
                      </div>
                    </label>
                    <label className="sms-option-card">
                      <input
                        type="checkbox"
                        checked={Boolean(twilioForm.smsSchedule?.send1hReminder)}
                        onChange={(e) => setTwilioForm({
                          ...twilioForm,
                          smsSchedule: {
                            ...twilioForm.smsSchedule,
                            send1hReminder: e.target.checked,
                          },
                        })}
                      />
                      <div>
                        <strong>1 Hour Reminder</strong>
                        <p>Send reminder 1 hour before appointment</p>
                      </div>
                    </label>
                  </div>
                  <p className="hint" style={{ fontSize: '0.85em', marginTop: '6px' }}>24-hour and 1-hour reminders can both be enabled.</p>
                </div>
              </div>
              <div className="form-row-1">
                <div>
                  <label>Reminder Message Template</label>
                  <textarea
                    value={twilioForm.reminderMessageTemplate}
                    onChange={(e) => setTwilioForm({ ...twilioForm, reminderMessageTemplate: e.target.value })}
                    placeholder="Hi {name}, reminder: appointment at {time} on {date}. Reply STOP to opt out."
                    rows={3}
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                  <p className="hint" style={{ fontSize: '0.85em', marginTop: '4px' }}>Available: {'{name}'} {'{time}'} {'{date}'} {'{tech}'} {'{service}'}</p>
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-add">Save Reminder Settings</button>
                {smsMsg && <span className="hint ok">{smsMsg}</span>}
              </div>

              <hr />
              <h4>Send Test SMS</h4>
              <div className="form-row-2">
                <div>
                  <label>To Phone Number</label>
                  <input
                    value={testTo}
                    onChange={(e) => setTestTo(formatUSPhoneInput(e.target.value))}
                    onBlur={() => setTestTo((prev) => toTwilioUSPhone(prev))}
                    placeholder="(702) 555-1234"
                  />
                </div>
                <div>
                  <label>Message</label>
                  <input value={testMessage} onChange={(e) => setTestMessage(e.target.value)} />
                </div>
              </div>
              <div className="form-actions">
                <button className="btn-add" onClick={sendTestSMS}>Send Test SMS</button>
                {testStatus && <span className="hint">{testStatus}</span>}
              </div>
            </form>
          </div>
        )}
      </div>

      <div className="sms-settings">
        <div className="sms-settings-header">
          <h3>Deposit Payment Settings (Zelle / CashApp)</h3>
          <button type="button" className="btn-add" onClick={() => setShowPaymentSettings((s) => !s)}>
            {showPaymentSettings ? 'Hide' : 'Configure'}
          </button>
        </div>

        {showPaymentSettings && (
          <div className="sms-settings-body">
            <form className="twilio-form" onSubmit={savePaymentSettings}>
              <p className="hint" style={{ marginTop: '0' }}>
                These details are shown to clients after they book a service that requires a
                deposit, so they know where to send it.
              </p>
              <div className="form-row-1">
                <div>
                  <label>Zelle Email</label>
                  <input
                    value={paymentForm.zelleEmail}
                    onChange={(e) => setPaymentForm({ ...paymentForm, zelleEmail: e.target.value })}
                    placeholder="payments@yoursalon.com"
                  />
                </div>
                <div>
                  <label>Zelle Phone</label>
                  <input
                    value={paymentForm.zellePhone}
                    onChange={(e) => setPaymentForm({ ...paymentForm, zellePhone: formatUSPhoneInput(e.target.value) })}
                    placeholder="+1-702-555-1234"
                  />
                </div>
              </div>
              <div className="form-row-1">
                <div>
                  <label>CashApp Tag</label>
                  <input
                    value={paymentForm.cashappTag}
                    onChange={(e) => setPaymentForm({ ...paymentForm, cashappTag: e.target.value })}
                    placeholder="$YourTag"
                  />
                </div>
              </div>
              <div className="form-row-1">
                <div>
                  <label>Deposit Note (optional)</label>
                  <textarea
                    value={paymentForm.depositNote}
                    onChange={(e) => setPaymentForm({ ...paymentForm, depositNote: e.target.value })}
                    placeholder="Please include your name so we can match your deposit to your booking."
                    rows={3}
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-add" disabled={paymentLoading}>
                  Save Payment Settings
                </button>
                {paymentMsg && <span className="hint ok">{paymentMsg}</span>}
              </div>
            </form>
          </div>
        )}
      </div>

      <div className="sms-settings legal-settings">
        <div className="sms-settings-header">
          <h3>Legal Pages Content</h3>
          <button type="button" className="btn-add" onClick={() => setShowLegalContent((s) => !s)}>
            {showLegalContent ? 'Hide' : 'Configure'}
          </button>
        </div>

        {showLegalContent && (
          <form className="twilio-form" onSubmit={saveSMSSettings}>
            <div className="form-row-1">
              <div>
                <label>Privacy Policy Content</label>
                <textarea
                  value={twilioForm.privacyPolicyContent}
                  onChange={(e) => setTwilioForm({ ...twilioForm, privacyPolicyContent: e.target.value })}
                  placeholder="Write your Privacy Policy text here..."
                  rows={8}
                  style={{ width: '100%', resize: 'vertical' }}
                />
                <p className="hint" style={{ fontSize: '0.85em', marginTop: '4px' }}>Published at /privacy</p>
              </div>
            </div>
            <div className="form-row-1">
              <div>
                <label>Terms of Service Content</label>
                <textarea
                  value={twilioForm.termsOfServiceContent}
                  onChange={(e) => setTwilioForm({ ...twilioForm, termsOfServiceContent: e.target.value })}
                  placeholder="Write your Terms of Service text here..."
                  rows={8}
                  style={{ width: '100%', resize: 'vertical' }}
                />
                <p className="hint" style={{ fontSize: '0.85em', marginTop: '4px' }}>Published at /ToS</p>
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-add">Save Legal Content</button>
              {smsMsg && <span className="hint ok">{smsMsg}</span>}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
